/* eslint-disable max-lines */
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '@components/layout/page-header';
import Tabs, { Tab } from '@components/atoms/ui-kit/tabs';
import {
  Users,
  Building2,
  Handshake,
  Receipt,
  PlusCircle,
  Server
} from 'lucide-react';

// Components
import SummaryCards from '@components/back-office/usage-revenue/summary-cards';
import MonthSelector from '@components/back-office/usage-revenue/month-selector';
import UsageTable from '@components/back-office/usage-revenue/usage-table';
import ResellerTable from '@components/back-office/usage-revenue/reseller-table';
import BillablesBo from '@pages/back-office/billables-bo';
import OneOffChargesList from '@components/back-office/usage-revenue/one-off-charges-list';
import OnPremBillingTab from '@components/back-office/usage-revenue/on-prem-billing-tab';

// Types
import {
  UsageDetailedFilters,
  ResellerSummaryFilters
} from '@utils/types/back-office/usage-revenue';

// Hooks
import {
  useGetUsageSummary,
  useGetUsageDetailed,
  useGetResellerSummary
} from '@utils/api/back-office/usage-revenue';

export enum UsageRevenueTabs {
  AllCustomers = 'All Customers',
  DirectOnly = 'Direct Only',
  ResellersOnly = 'Resellers Only',
  Billables = 'Billables',
  OneOffCharges = 'One-Off Charges',
  OnPremBilling = 'On-Prem Billing'
}

const DEFAULT_PAGE_SIZE = 25;

export default function UsageRevenueBo() {
  const { t } = useTranslation();

  // Get current month in YYYY-MM format
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());

  // Pagination, sorting, and filter state for All Customers tab
  const [allCustomersOffset, setAllCustomersOffset] = useState(0);
  const [allCustomersSort, setAllCustomersSort] = useState<string | undefined>(
    'stt_units'
  );
  const [allCustomersDirection, setAllCustomersDirection] = useState<
    'asc' | 'desc'
  >('desc');
  const [allCustomersFilters, setAllCustomersFilters] =
    useState<UsageDetailedFilters>({});

  // Pagination, sorting, and filter state for Direct Only tab
  const [directOffset, setDirectOffset] = useState(0);
  const [directSort, setDirectSort] = useState<string | undefined>('stt_units');
  const [directDirection, setDirectDirection] = useState<'asc' | 'desc'>(
    'desc'
  );
  const [directFilters, setDirectFilters] = useState<UsageDetailedFilters>({});

  // Pagination, sorting, and filter state for Resellers tab
  const [resellersOffset, setResellersOffset] = useState(0);
  const [resellersSort, setResellersSort] = useState<string | undefined>(
    'reseller_name'
  );
  const [resellersDirection, setResellersDirection] = useState<'asc' | 'desc'>(
    'asc'
  );
  const [resellersFilters, setResellersFilters] =
    useState<ResellerSummaryFilters>({});

  // Fetch summary data (replaces the old forecast endpoint)
  const { data: summaryData, isLoading: summaryLoading } =
    useGetUsageSummary(selectedMonth);

  // Fetch all customers usage data
  const { data: allUsageData, isLoading: allUsageLoading } =
    useGetUsageDetailed(selectedMonth, {
      customerType: 'all',
      limit: DEFAULT_PAGE_SIZE,
      offset: allCustomersOffset,
      sort: allCustomersSort,
      direction: allCustomersDirection,
      ...allCustomersFilters
    });

  // Fetch direct customers usage data
  const { data: directUsageData, isLoading: directUsageLoading } =
    useGetUsageDetailed(selectedMonth, {
      customerType: 'direct',
      limit: DEFAULT_PAGE_SIZE,
      offset: directOffset,
      sort: directSort,
      direction: directDirection,
      ...directFilters
    });

  // Fetch reseller summary data
  const { data: resellerData, isLoading: resellerLoading } =
    useGetResellerSummary(selectedMonth, {
      limit: DEFAULT_PAGE_SIZE,
      offset: resellersOffset,
      sort: resellersSort,
      direction: resellersDirection,
      ...resellersFilters
    });

  // Extract summary values from summary data
  const summaryValues = {
    totalUsage: summaryData?.result?.total_usage ?? 0,
    totalBilled: summaryData?.result?.total_billed ?? 0,
    forecast: summaryData?.result?.forecast ?? 0,
    byCurrency: summaryData?.result?.by_currency
  };

  // Pagination handlers for All Customers
  const handleAllCustomersPaginationChange = useCallback((offset: number) => {
    setAllCustomersOffset(offset);
  }, []);

  const handleAllCustomersSortChange = useCallback(
    (sort: string | undefined, direction: 'asc' | 'desc') => {
      setAllCustomersSort(sort);
      setAllCustomersDirection(direction);
    },
    []
  );

  // Pagination handlers for Direct Only
  const handleDirectPaginationChange = useCallback((offset: number) => {
    setDirectOffset(offset);
  }, []);

  const handleDirectSortChange = useCallback(
    (sort: string | undefined, direction: 'asc' | 'desc') => {
      setDirectSort(sort);
      setDirectDirection(direction);
    },
    []
  );

  // Pagination handlers for Resellers
  const handleResellersPaginationChange = useCallback((offset: number) => {
    setResellersOffset(offset);
  }, []);

  const handleResellersSortChange = useCallback(
    (sort: string | undefined, direction: 'asc' | 'desc') => {
      setResellersSort(sort);
      setResellersDirection(direction);
    },
    []
  );

  // Filter handlers — reset offset when filters change
  const handleAllCustomersFilterChange = useCallback(
    (filters: UsageDetailedFilters) => {
      setAllCustomersFilters(filters);
      setAllCustomersOffset(0);
    },
    []
  );

  const handleDirectFilterChange = useCallback(
    (filters: UsageDetailedFilters) => {
      setDirectFilters(filters);
      setDirectOffset(0);
    },
    []
  );

  const handleResellersFilterChange = useCallback(
    (filters: ResellerSummaryFilters) => {
      setResellersFilters(filters);
      setResellersOffset(0);
    },
    []
  );

  const tabs: Tab[] = [
    {
      id: 0,
      name: (
        <span className="flex items-center gap-2">
          <Users className="h-4 w-4 shrink-0" />
          {UsageRevenueTabs.AllCustomers}
        </span>
      ),
      component: (
        <div className="py-4 flex-1 min-h-0 flex flex-col">
          <UsageTable
            data={allUsageData?.result ?? []}
            isLoading={allUsageLoading}
            totalItems={allUsageData?.total_results ?? 0}
            pageSize={DEFAULT_PAGE_SIZE}
            offset={allCustomersOffset}
            onPaginationChange={handleAllCustomersPaginationChange}
            onSortChange={handleAllCustomersSortChange}
            onFilterChange={handleAllCustomersFilterChange}
            defaultSort={{ id: 'usage', desc: true }}
          />
        </div>
      )
    },
    {
      id: 1,
      name: (
        <span className="flex items-center gap-2">
          <Building2 className="h-4 w-4 shrink-0" />
          {UsageRevenueTabs.DirectOnly}
        </span>
      ),
      component: (
        <div className="py-4 flex-1 min-h-0 flex flex-col">
          <UsageTable
            data={directUsageData?.result ?? []}
            isLoading={directUsageLoading}
            totalItems={directUsageData?.total_results ?? 0}
            pageSize={DEFAULT_PAGE_SIZE}
            offset={directOffset}
            onPaginationChange={handleDirectPaginationChange}
            onSortChange={handleDirectSortChange}
            onFilterChange={handleDirectFilterChange}
            defaultSort={{ id: 'usage', desc: true }}
          />
        </div>
      )
    },
    {
      id: 2,
      name: (
        <span className="flex items-center gap-2">
          <Handshake className="h-4 w-4 shrink-0" />
          {UsageRevenueTabs.ResellersOnly}
        </span>
      ),
      component: (
        <div className="py-4 flex-1 min-h-0 flex flex-col">
          <ResellerTable
            data={resellerData?.result ?? []}
            isLoading={resellerLoading}
            totalItems={resellerData?.total_results ?? 0}
            pageSize={DEFAULT_PAGE_SIZE}
            offset={resellersOffset}
            onPaginationChange={handleResellersPaginationChange}
            onSortChange={handleResellersSortChange}
            onFilterChange={handleResellersFilterChange}
            defaultSort={{ id: 'reseller_name', desc: false }}
          />
        </div>
      )
    },
    {
      id: 3,
      name: (
        <span className="flex items-center gap-2">
          <PlusCircle className="h-4 w-4 shrink-0" />
          {UsageRevenueTabs.OneOffCharges}
        </span>
      ),
      component: <OneOffChargesList />
    },
    {
      id: 4,
      name: (
        <span className="flex items-center gap-2">
          <Server className="h-4 w-4 shrink-0" />
          {UsageRevenueTabs.OnPremBilling}
        </span>
      ),
      component: <OnPremBillingTab />
    },
    {
      id: 5,
      name: (
        <span className="flex items-center gap-2">
          <Receipt className="h-4 w-4 shrink-0" />
          {UsageRevenueTabs.Billables}
        </span>
      ),
      component: <BillablesBo />
    }
  ];

  return (
    <div className="bg-app flex-1 min-h-0 flex flex-col">
      <div className="px-4 py-2 flex-1 min-h-0 flex flex-col">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
          <PageHeader
            title={t('usage-and-revenue')}
            caption={t('view-usage-metrics-billing-and-revenue-forecasts')}
          />
          <MonthSelector value={selectedMonth} onChange={setSelectedMonth} />
        </div>

        <SummaryCards
          totalUsage={summaryValues.totalUsage}
          totalBilled={summaryValues.totalBilled}
          forecast={summaryValues.forecast}
          byCurrency={summaryValues.byCurrency}
          isLoading={summaryLoading}
          className="mb-6"
        />

        <div className="flex-1 min-h-0">
          <Tabs tabs={tabs} searchParams minHeight="0" maxHeight="100%" />
        </div>
      </div>
    </div>
  );
}
